import React, { useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Typography, TYPOGRAPHY_WEIGHT } from '_atoms/Typography';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { ANALYTICS_TABS } from 'constants/constants';
import { TransitionStep } from '_templates/AnalyticsDashboard/TransitionStep/transition-step';
import { AnalyticsEmpty } from '_templates/AnalyticsDashboard/AnalyticsEmpty/analytics-empty';
import {
  analyticWidgets,
  type PassingStepWidget,
  type RejectionNumberWidget,
  type RejectionReasonWidget,
  type TransitionStepWidget,
} from 'actions/analyticActions';
import { PassingStep } from '_templates/AnalyticsDashboard/PassingStep/passing-step';
import { Rejection } from '_templates/AnalyticsDashboard/Rejection/rejection';
import { Reason } from '_templates/AnalyticsDashboard/Reason/reason';
import {
  createInitialWidgetState,
  type AnalyticsAppliedFilters,
  type AssignedRawData,
  type JobOffersRawData,
} from '_templates/AnalyticsDashboard/types';
import Date from 'dist/images/common/icons/graph.svg';
import { ReactComponent as Clock } from 'dist/emptyStates/dashboardReminders.svg';
import { CandidateWidget } from '_templates/AnalyticsRecruitersInfo/AnalyticsRecruitersInfo';
import type { CandidateWidgetItem } from '_templates/AnalyticsRecruitersInfo/types';
import type { ClosureContentProps, ClosureTimeRawData, JobCandidateWidget } from './types';

const ClosureContent = ({ total, options }: ClosureContentProps) => {
  const { t } = useTranslation();
  return (
    <div className="widget-candidates-close-time">
      <div className="main-info">
        <Clock className="main-info__image" />
        <div className="main-info__total">
          {total.days}
          <div className="main-info__description">{t('translation:analytics_job_info.label_days_short')}</div>
          {total.hours}
          <div className="main-info__description">{t('translation:analytics_job_info.label_hours_short')}</div>
        </div>
      </div>
      <div className="description-info">
        {options.map(o => (
          <div className="opening-info" key={o.expected + o.jobName}>
            <div className="opening-info__info">
              <Typography.Title4 weight={TYPOGRAPHY_WEIGHT.BOLD}>{o.expected}</Typography.Title4>
              <Typography.Label>
                {o.level} {o.jobName}
              </Typography.Label>
            </div>
            <div className="opening-info__time">{o.time}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const closureContentRender = (data: ClosureContentProps | CandidateWidgetItem) => {
  const { total, options } = data as ClosureContentProps;
  return <ClosureContent total={total} options={options} />;
};

const assignedMapper = (values: JobCandidateWidget, data: AssignedRawData, t: TFunction): JobCandidateWidget => ({
  ...values,
  total: data.candidates,
  simpleView: true,
  options: [
    { label: t('translation:analytics_job_info.label_services'), value: data.services },
    { label: t('translation:analytics_job_info.label_jobs'), value: data.jobs.length },
  ],
  loading: false,
});

const closureTimeMapper = (values: JobCandidateWidget, data: ClosureTimeRawData): JobCandidateWidget => ({
  ...values,
  icon: null,
  total: { days: Math.floor(data.average_closure_hours / 24), hours: Math.floor(data.average_closure_hours % 24) },
  simpleView: true,
  options: data.openings.map(o => ({
    expected: moment(o.expected, 'YYYY-MM-DD').format('MMM DD, YYYY'),
    jobName: o.job_name,
    level: o.level,
    time: `${Math.floor(o.closure_hours / 24)}d ${Math.floor(o.closure_hours % 24)}h`,
  })),
  loading: false,
});

const offersMapper = (values: JobCandidateWidget, data: JobOffersRawData, t: TFunction): JobCandidateWidget => ({
  ...values,
  total: data.total,
  options: [
    { label: t('translation:analytics_job_info.label_waiting_for_feedback'), value: data.waiting_feedback },
    { label: t('translation:analytics_job_info.label_onboarding'), value: data.onboarding },
    { label: t('translation:analytics_job_info.label_rejected'), value: data.rejected },
  ],
  loading: false,
});

export const AnalyticsJobInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;

  const [totalAssigned, setTotalAssigned] = useState<JobCandidateWidget>({
    title: t('translation:analytics_job_info.title_assigned'),
    total: 0,
    simpleView: false,
    description: t('translation:analytics_job_info.label_candidates'),
    options: [],
    loading: false,
    empty: false,
  } as JobCandidateWidget);
  const [closureTime, setClosureTime] = useState<JobCandidateWidget>({
    title: t('translation:analytics_job_info.title_closure_time'),
    total: 0,
    simpleView: false,
    description: t('translation:analytics_job_info.label_interacted'),
    options: [],
    loading: false,
    empty: false,
    contentRender: closureContentRender,
  } as JobCandidateWidget);
  const [jobOffers, setJobOffers] = useState<JobCandidateWidget>({
    title: t('translation:analytics_job_info.title_job_offers'),
    total: 0,
    simpleView: false,
    description: t('translation:analytics_job_info.label_offers_made'),
    options: [],
    loading: false,
    empty: false,
  } as JobCandidateWidget);
  const [passingStep, setPassingStep] = useState(createInitialWidgetState<PassingStepWidget[]>([]));
  const [rejectionNumber, setRejectionNumber] = useState(createInitialWidgetState<RejectionNumberWidget[]>([]));
  const [rejectionReason, setRejectionReason] = useState(createInitialWidgetState<RejectionReasonWidget[]>([]));
  const [transitionStep, setTransitionStep] = useState(
    createInitialWidgetState<TransitionStepWidget['options']>([] as TransitionStepWidget['options'])
  );
  const [to, setTo] = useState<PassingStepWidget | Record<string, never>>({});
  const [from, setFrom] = useState<PassingStepWidget | Record<string, never>>({});

  const chosenTab = ANALYTICS_TABS.JOBS;

  const setLoading = () => {
    setRejectionNumber(createInitialWidgetState<RejectionNumberWidget[]>([]));
    setRejectionReason(createInitialWidgetState<RejectionReasonWidget[]>([]));
    setPassingStep(createInitialWidgetState<PassingStepWidget[]>([]));
    setTransitionStep(createInitialWidgetState<TransitionStepWidget['options']>([] as TransitionStepWidget['options']));
    setTotalAssigned(v => ({ ...v, loading: true }));
    setClosureTime(v => ({ ...v, loading: true }));
    setJobOffers(v => ({ ...v, loading: true }));
  };

  const applyAnalyticFilter = useCallback((f: AnalyticsAppliedFilters) => {
    setLoading();

    analyticWidgets('total-assigned', f, null).then(resp =>
      setTotalAssigned(v => assignedMapper(v, resp as AssignedRawData, t))
    );
    analyticWidgets('openings-closure-time', f, null).then(resp =>
      setClosureTime(v => closureTimeMapper(v, resp as ClosureTimeRawData))
    );
    analyticWidgets('job-offers', f, null).then(resp =>
      setJobOffers(v => offersMapper(v, resp as JobOffersRawData, t))
    );
    analyticWidgets('rejections-number', f, 'number').then(resp =>
      setRejectionNumber({ options: resp, loading: false })
    );
    analyticWidgets('rejection-reasons', f, 'reason').then(resp => {
      setRejectionReason({ options: resp, loading: false });
    });
    analyticWidgets('steps-transition', f, 'transition').then(resp =>
      setTransitionStep({ options: resp.options, empty: resp.empty, loading: false })
    );
    analyticWidgets('steps-passing-time', f, 'passing').then(resp => {
      setPassingStep({ options: resp, empty: !resp.filter(item => !!item.data).length, loading: false });
      setFrom(resp[0]);
      setTo(resp[resp.length - 1]);
    });
  }, []);

  useEffect(() => {
    applyAnalyticFilter(appliedFilters);
  }, [appliedFilters]);

  return transitionStep.loading || (!transitionStep.loading && (!transitionStep.empty || !passingStep.empty)) ? (
    <>
      <Row gutter={[32, 0]}>
        <Col>
          <CandidateWidget
            className="fill-height"
            data={[totalAssigned, closureTime, jobOffers] as CandidateWidgetItem[]}
          />
        </Col>
        <Col>
          <Reason className="fill-height" options={rejectionReason.options} loading={rejectionReason.loading} />
        </Col>
      </Row>
      <TransitionStep
        options={transitionStep.options}
        loading={transitionStep.loading}
        empty={transitionStep.empty}
        role={chosenTab}
      />
      <PassingStep
        options={passingStep.options}
        loading={passingStep.loading}
        from={from}
        to={to}
        setFrom={setFrom}
        setTo={setTo}
      />
      <Rejection options={rejectionNumber.options} loading={rejectionNumber.loading} />
    </>
  ) : (
    <AnalyticsEmpty
      text={t('translation:analytics_job_info.text_no_results')}
      title={t('translation:analytics_job_info.title_no_results')}
      className="shadow"
      img={Date}
    />
  );
};
